/**
 * V3 — Devis multi-entités (CRUD complet).
 * Gestion dynamique des lignes article (description / quantité / prix unitaire),
 * calcul auto HT/TVA/TTC, échéance, statuts Brouillon/Envoyé/Accepté/Refusé/Expiré.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from './api';
import {
  Download, Search, ChevronLeft, ChevronRight, FileText, Briefcase,
  Plus, Pencil, Trash2, X, AlertTriangle, CheckCircle2, Truck,
} from 'lucide-react';

const baseURL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const ENTITY_COLORS = {
  CIS: '#22c55e', ROPS: '#f59e0b', NETBTP: '#ef4444',
  NKEYI: '#8b5cf6', R2S: '#fb923c', EXCUISINE: '#6366f1',
};
const ENTITIES = ['ROPS', 'NETBTP', 'CIS', 'NKEYI', 'R2S', 'EXCUISINE'];
const STATUTS = ['Brouillon', 'Envoyé', 'Accepté', 'Refusé', 'Expiré'];

const STATUS_PILL = (s) => {
  const lower = (s || '').toLowerCase();
  if (lower.includes('accept')) return 'v3-pill-in';
  if (lower.includes('refus') || lower.includes('expir')) return 'v3-pill-out';
  if (lower.includes('envoy')) return 'v3-pill-cat';
  return 'v3-pill-cat';
};

const emptyLigne = () => ({ description: '', quantite: 1, prixUnitaire: 0 });

const EMPTY_FORM = {
  entity: 'ROPS',
  date: new Date().toISOString().slice(0, 10),
  echeance: '',
  numero: '',
  client: '',
  clientAddr: '',
  clientNIF: '',
  statut: 'Brouillon',
  lignes: [emptyLigne()],
  tva_taux: 18,
  notes: '',
  conditions: '',
};

export default function V3Devis() {
  const [data, setData] = useState({
    items: [], total: 0, total_pages: 0,
    kpis: { total_ht: 0, total_ttc: 0, count: 0 },
    entities_available: [], statuts_available: [],
  });
  const [entity, setEntity] = useState('ALL');
  const [statut, setStatut] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // CRUD
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const totals = useMemo(() => {
    const ht = (formData.lignes || []).reduce((acc, ln) => {
      return acc + (Number(ln.quantite) || 0) * (Number(ln.prixUnitaire) || 0);
    }, 0);
    const taux = Number(formData.tva_taux) || 0;
    const taxes = ht * taux / 100;
    return { ht: round2(ht), taxes: round2(taxes), ttc: round2(ht + taxes) };
  }, [formData.lignes, formData.tva_taux]);

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM, entity: entity === 'ALL' ? 'ROPS' : entity, lignes: [emptyLigne()] });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = async (dv) => {
    setBusy(true); setFormError(null);
    try {
      const { data: detail } = await api.get(`/api/v3/devis/${dv.id}`, { params: { entity: dv.entity } });
      const full = detail.devis;
      const taxes = full.taxes || [];
      const tvaLigne = taxes.find(t => (t.nom || '').toLowerCase().includes('tva')) || taxes[0];
      setEditingId(full.id);
      setFormData({
        entity: full.entity || 'ROPS',
        date: full.date || new Date().toISOString().slice(0, 10),
        echeance: full.echeance || '',
        numero: full.numero || '',
        client: full.client || '',
        clientAddr: full.client_addr || '',
        clientNIF: full.client_nif || '',
        statut: full.statut || 'Brouillon',
        lignes: (full.lignes && full.lignes.length > 0)
          ? full.lignes.map(l => ({
              description: l.description || '',
              quantite: Number(l.quantite) || 0,
              prixUnitaire: Number(l.prixUnitaire) || 0,
            }))
          : [emptyLigne()],
        tva_taux: tvaLigne ? Number(tvaLigne.taux) || 0 : 0,
        notes: full.notes || '',
        conditions: full.conditions || '',
      });
      setShowForm(true);
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur chargement', 'error');
    } finally {
      setBusy(false);
    }
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setFormError(null); };

  const addLigne = () => setFormData({ ...formData, lignes: [...formData.lignes, emptyLigne()] });
  const removeLigne = (idx) => {
    if (formData.lignes.length <= 1) return;
    setFormData({ ...formData, lignes: formData.lignes.filter((_, i) => i !== idx) });
  };
  const updateLigne = (idx, patch) => {
    setFormData({ ...formData, lignes: formData.lignes.map((ln, i) => i === idx ? { ...ln, ...patch } : ln) });
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!formData.date) { setFormError('Date obligatoire.'); return; }
    if (!formData.client.trim()) { setFormError('Client obligatoire.'); return; }
    if (formData.lignes.length === 0) { setFormError('Au moins une ligne.'); return; }
    for (let i = 0; i < formData.lignes.length; i++) {
      const ln = formData.lignes[i];
      if (!ln.description.trim()) { setFormError(`Ligne ${i+1} : description obligatoire.`); return; }
      const q = Number(ln.quantite), pu = Number(ln.prixUnitaire);
      if (!q || q <= 0) { setFormError(`Ligne ${i+1} : quantité > 0.`); return; }
      if (isNaN(pu) || pu < 0) { setFormError(`Ligne ${i+1} : prix unitaire ≥ 0.`); return; }
    }
    setBusy(true); setFormError(null);
    try {
      const taxes = formData.tva_taux && Number(formData.tva_taux) > 0
        ? [{ nom: 'TVA', taux: Number(formData.tva_taux) }] : [];
      const payload = {
        entity: formData.entity,
        date: formData.date,
        echeance: formData.echeance || null,
        numero: formData.numero.trim() || null,
        client: formData.client.trim(),
        clientAddr: formData.clientAddr.trim() || null,
        clientNIF: formData.clientNIF.trim() || null,
        statut: formData.statut,
        lignes: formData.lignes.map(ln => ({
          description: ln.description.trim(),
          quantite: Number(ln.quantite),
          prixUnitaire: Number(ln.prixUnitaire),
        })),
        taxes,
        notes: formData.notes.trim() || null,
        conditions: formData.conditions.trim() || null,
      };
      if (editingId) {
        await api.put(`/api/v3/devis/${editingId}`, payload);
        showToast(`Devis mis à jour (TTC ${fmt(totals.ttc)} FCFA)`);
      } else {
        const { data: resp } = await api.post('/api/v3/devis', payload);
        const num = resp?.devis?.numero || '';
        showToast(`Devis ${num} créé · TTC ${fmt(totals.ttc)} FCFA`);
      }
      closeForm();
      fetchData();
    } catch (e) {
      setFormError(e?.response?.data?.detail || e.message || 'Erreur réseau');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/api/v3/devis/${confirmDelete.id}?entity=${encodeURIComponent(confirmDelete.entity)}`);
      showToast(`Devis ${confirmDelete.numero || ''} supprimé`);
      setConfirmDelete(null);
      fetchData();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  const convertToBL = async (dv) => {
    if (!window.confirm(`Convertir le devis ${dv.numero || dv.id} (${fmt(dv.montant_ttc)} FCFA TTC) en bon de livraison ?`)) return;
    setBusy(true);
    try {
      const { data: resp } = await api.post(
        `/api/v3/devis/${dv.id}/convert-to-bl?entity=${encodeURIComponent(dv.entity)}`
      );
      showToast(`BL ${resp.bl.numero} créé (${fmt(resp.bl.montant_ttc)} FCFA)`);
      fetchData();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { entity, page, page_size: 30 };
      if (statut) params.statut = statut;
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (search) params.search = search;
      const { data: d } = await api.get('/api/v3/devis', { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [entity, statut, from, to, search, page]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  const exportUrl = () => {
    const p = new URLSearchParams();
    p.set('entity', entity);
    if (from) p.set('from_date', from);
    if (to) p.set('to_date', to);
    return `${baseURL}/api/v3/devis/export.xlsx?${p}`;
  };

  return (
    <div className="v3-page" data-testid="v3-devis">
      <div className="v3-page-head">
        <h1>Devis</h1>
        <p>{data.total} devis · {entity === 'ALL' ? 'toutes entités' : entity}</p>
      </div>

      <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <KpiCard icon={<Briefcase/>} label="Nb devis" value={data.kpis.count} accent="cyan" />
        <KpiCard icon={<FileText/>} label="Total HT" value={fmt(data.kpis.total_ht) + ' FCFA'} accent="amber" />
        <KpiCard icon={<FileText/>} label="Total TTC" value={fmt(data.kpis.total_ttc) + ' FCFA'} accent="emerald" />
      </section>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Entité</label>
          <select value={entity} onChange={(e) => { setPage(1); setEntity(e.target.value); }} data-testid="filter-entity">
            <option value="ALL">Toutes</option>
            {data.entities_available.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Statut</label>
          <select value={statut} onChange={(e) => { setPage(1); setStatut(e.target.value); }} data-testid="filter-statut">
            <option value="">Tous</option>
            {data.statuts_available.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} />
        </div>
        <div className="v3-filter-grp v3-filter-search">
          <label>Recherche</label>
          <div className="v3-search-input">
            <Search size={14}/>
            <input type="text" placeholder="Client, N°, notes…" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
          </div>
        </div>
        <button type="button" onClick={openCreate} className="v3-btn v3-btn-primary" data-testid="btn-new-devis">
          <Plus size={16}/>Nouveau devis
        </button>
        <a href={exportUrl()} className="v3-btn v3-btn-secondary" data-testid="btn-export-devis">
          <Download size={16}/>Excel
        </a>
      </section>

      <section className="v3-card v3-card-table">
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Échéance</th>
                <th>Entité</th>
                <th>N°</th>
                <th>Client</th>
                <th>Lignes</th>
                <th className="num">HT</th>
                <th className="num">TTC</th>
                <th>Statut</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="v3-row" data-testid={`devis-row-${it.id}`}>
                  <td className="mono">{it.date}</td>
                  <td className="mono">{it.echeance || '—'}</td>
                  <td><span className="v3-entity-pill" style={{ background: (ENTITY_COLORS[it.entity] || '#94a3b8') + '20', color: ENTITY_COLORS[it.entity] || '#94a3b8' }}>{it.entity}</span></td>
                  <td className="mono">{it.numero}</td>
                  <td>{it.client || '—'}</td>
                  <td className="num">{it.lignes_count}</td>
                  <td className="num">{fmt(it.montant_ht)}</td>
                  <td className="num strong">{fmt(it.montant_ttc)}</td>
                  <td><span className={`v3-pill ${STATUS_PILL(it.statut)}`}>{it.statut || '—'}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {it.bl_ref ? (
                      <span
                        className="v3-pill v3-pill-in"
                        title={`Bonné : ${it.bl_numero || it.bl_ref}`}
                        data-testid={`devis-bl-ref-${it.id}`}
                        style={{ fontSize: 10, padding: '3px 6px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Truck size={10} />
                        {it.bl_numero || 'Bonné'}
                      </span>
                    ) : (it.statut === 'Accepté') ? (
                      <button
                        onClick={() => convertToBL(it)}
                        disabled={busy}
                        title="Convertir en BL"
                        aria-label="Convertir en BL"
                        data-testid={`convert-devis-${it.id}`}
                        className="v3-icon-btn"
                        style={{ marginRight: 4 }}
                      ><Truck size={13} /></button>
                    ) : null}
                    <button
                      onClick={() => openEdit(it)}
                      title="Modifier"
                      aria-label="Modifier"
                      data-testid={`edit-devis-${it.id}`}
                      className="v3-icon-btn"
                      style={{ marginRight: 4 }}
                    ><Pencil size={13} /></button>
                    <button
                      onClick={() => setConfirmDelete({
                        id: it.id, entity: it.entity, numero: it.numero,
                        client: it.client, montant_ttc: it.montant_ttc,
                      })}
                      title="Supprimer"
                      aria-label="Supprimer"
                      data-testid={`delete-devis-${it.id}`}
                      className="v3-icon-btn v3-icon-btn-danger"
                    ><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && <tr><td colSpan={10} className="v3-empty">Aucun devis.</td></tr>}
              {loading && <tr><td colSpan={10} className="v3-empty">Chargement…</td></tr>}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="v3-pagination">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}><ChevronLeft size={16}/>Préc.</button>
            <span>Page {page} / {data.total_pages}</span>
            <button onClick={() => setPage(p => Math.min(data.total_pages, p+1))} disabled={page >= data.total_pages}>Suiv.<ChevronRight size={16}/></button>
          </div>
        )}
      </section>

      {showForm && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
          <form className="v3-modal v3-modal-wide" onSubmit={submitForm} data-testid="devis-form">
            <div className="v3-modal-head">
              <h2>{editingId ? 'Modifier un devis' : 'Nouveau devis'}</h2>
              <button type="button" onClick={closeForm} aria-label="Fermer" className="v3-icon-btn"><X size={16} /></button>
            </div>
            <div className="v3-modal-body">
              <div className="v3-form-row">
                <FormField label="Entité *">
                  <select required value={formData.entity}
                    onChange={(e) => setFormData({ ...formData, entity: e.target.value })}
                    disabled={!!editingId} data-testid="devis-form-entity">
                    {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </FormField>
                <FormField label="Date *">
                  <input type="date" required value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="devis-form-date" />
                </FormField>
                <FormField label="Échéance">
                  <input type="date" value={formData.echeance}
                    onChange={(e) => setFormData({ ...formData, echeance: e.target.value })}
                    data-testid="devis-form-echeance" />
                </FormField>
              </div>

              <div className="v3-form-row">
                <FormField label="Statut">
                  <select value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                    data-testid="devis-form-statut">
                    {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </FormField>
                <FormField label="N° (auto si vide)">
                  <input type="text" value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="DEVIS-ROPS-2026-0001" data-testid="devis-form-numero" />
                </FormField>
                <FormField label="NIF Client">
                  <input type="text" value={formData.clientNIF}
                    onChange={(e) => setFormData({ ...formData, clientNIF: e.target.value })}
                    placeholder="GA-XXXXX" data-testid="devis-form-client-nif" />
                </FormField>
              </div>

              <FormField label="Client *">
                <input type="text" required value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  placeholder="Raison sociale" data-testid="devis-form-client" autoFocus />
              </FormField>

              <FormField label="Adresse client">
                <input type="text" value={formData.clientAddr}
                  onChange={(e) => setFormData({ ...formData, clientAddr: e.target.value })}
                  placeholder="Adresse complète" data-testid="devis-form-client-addr" />
              </FormField>

              {/* === LIGNES ARTICLE === */}
              <div className="v3-form-field" style={{ marginTop: 16 }}>
                <label className="v3-form-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Lignes article *</span>
                  <button type="button" onClick={addLigne} className="v3-btn v3-btn-secondary v3-btn-sm" data-testid="devis-form-add-ligne">
                    <Plus size={12}/>Ajouter
                  </button>
                </label>
                <div className="v3-bl-lines">
                  {formData.lignes.map((ln, idx) => (
                    <div key={idx} className="v3-bl-line" data-testid={`devis-form-ligne-${idx}`}>
                      <input type="text" value={ln.description}
                        onChange={(e) => updateLigne(idx, { description: e.target.value })}
                        placeholder="Description" data-testid={`devis-form-ligne-desc-${idx}`} style={{ flex: 3 }} />
                      <input type="number" min="0" step="0.01" value={ln.quantite}
                        onChange={(e) => updateLigne(idx, { quantite: e.target.value })}
                        placeholder="Qté" data-testid={`devis-form-ligne-qte-${idx}`} style={{ flex: 1, minWidth: 80 }} />
                      <input type="number" min="0" step="0.01" value={ln.prixUnitaire}
                        onChange={(e) => updateLigne(idx, { prixUnitaire: e.target.value })}
                        placeholder="PU" data-testid={`devis-form-ligne-pu-${idx}`} style={{ flex: 1.2, minWidth: 100 }} />
                      <div className="v3-bl-line-total" style={{ flex: 1.2, minWidth: 100, textAlign: 'right', fontFamily: 'monospace' }}>
                        {fmt((Number(ln.quantite) || 0) * (Number(ln.prixUnitaire) || 0))}
                      </div>
                      <button type="button" onClick={() => removeLigne(idx)}
                        disabled={formData.lignes.length <= 1}
                        className="v3-icon-btn v3-icon-btn-danger" aria-label="Supprimer la ligne"
                        data-testid={`devis-form-ligne-remove-${idx}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="v3-form-row" style={{ marginTop: 16 }}>
                <FormField label="TVA (%)">
                  <input type="number" min="0" max="100" step="0.1" value={formData.tva_taux}
                    onChange={(e) => setFormData({ ...formData, tva_taux: e.target.value })}
                    placeholder="18" data-testid="devis-form-tva-taux" />
                </FormField>
                <div className="v3-form-field" style={{ gridColumn: 'span 2' }}>
                  <label className="v3-form-field-label">Totaux</label>
                  <div className="v3-bl-totals" data-testid="devis-form-totals">
                    <div><span>HT :</span><strong data-testid="devis-form-total-ht">{fmt(totals.ht)} FCFA</strong></div>
                    <div><span>TVA :</span><strong data-testid="devis-form-total-taxes">{fmt(totals.taxes)} FCFA</strong></div>
                    <div className="v3-bl-totals-ttc"><span>TTC :</span><strong data-testid="devis-form-total-ttc">{fmt(totals.ttc)} FCFA</strong></div>
                  </div>
                </div>
              </div>

              <FormField label="Conditions commerciales">
                <input type="text" value={formData.conditions}
                  onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
                  placeholder="Paiement 30 jours net, validité 60 jours…" data-testid="devis-form-conditions" />
              </FormField>

              <FormField label="Notes">
                <textarea value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes facultatives" rows={2} data-testid="devis-form-notes" />
              </FormField>

              {formError && (
                <div className="v3-form-error">
                  <AlertTriangle size={14} /> {formError}
                </div>
              )}
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={closeForm} className="v3-btn v3-btn-secondary" data-testid="devis-form-cancel">Annuler</button>
              <button type="submit" disabled={busy} className="v3-btn v3-btn-primary" data-testid="devis-form-submit">
                {busy ? 'Enregistrement…' : (editingId ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div className="v3-modal v3-modal-confirm">
            <div className="v3-modal-body" style={{ textAlign: 'center' }}>
              <AlertTriangle size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
              <h2 style={{ marginBottom: 6 }}>Confirmer la suppression</h2>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                Supprimer le devis <b>{confirmDelete.numero || confirmDelete.id}</b>
                {confirmDelete.client ? <> · <b>{confirmDelete.client}</b></> : null}
                {confirmDelete.montant_ttc ? <> · <b>{fmt(confirmDelete.montant_ttc)} FCFA TTC</b></> : null}
                {' '}({confirmDelete.entity}) ? Cette action est synchronisée avec l&apos;app legacy.
              </p>
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={() => setConfirmDelete(null)} className="v3-btn v3-btn-secondary">Annuler</button>
              <button type="button" onClick={doDelete} disabled={busy} className="v3-btn v3-btn-danger" data-testid="confirm-delete-devis-btn">
                {busy ? 'Suppression…' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`v3-toast v3-toast-${toast.type}`} data-testid="devis-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}

      <style>{`
        .v3-modal-overlay { position: fixed; inset: 0; background: rgba(10,22,40,0.85); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
        .v3-modal { background: #0f1e3a; border: 1px solid rgba(214,243,47,0.15); border-radius: 18px; max-width: 540px; width: 100%; color: #e5e7eb; box-shadow: 0 30px 80px rgba(0,0,0,0.6); max-height: 90vh; display: flex; flex-direction: column; }
        .v3-modal-wide { max-width: 920px; }
        .v3-modal-confirm { max-width: 420px; }
        .v3-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .v3-modal-head h2 { font-size: 17px; font-weight: 800; color: #fff; letter-spacing: -0.3px; margin: 0; }
        .v3-modal-body { padding: 24px; overflow-y: auto; }
        .v3-modal-foot { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.06); }
        .v3-form-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
        .v3-modal:not(.v3-modal-wide) .v3-form-row { grid-template-columns: 1fr 1fr; }
        .v3-form-error { margin-top: 14px; padding: 12px 14px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #fca5a5; font-size: 12px; border-radius: 8px; display: flex; align-items: center; gap: 8px; }
        .v3-form-field { margin-bottom: 16px; }
        .v3-form-field-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 1.2px; color: #71717a; font-weight: 700; margin-bottom: 6px; }
        .v3-form-field input, .v3-form-field select, .v3-form-field textarea { width: 100%; padding: 12px 14px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: #fff; font: 500 13px Inter; transition: all .2s; }
        .v3-form-field input:focus, .v3-form-field select:focus, .v3-form-field textarea:focus { outline: none; border-color: #D6F32F; background: rgba(0,0,0,0.4); box-shadow: 0 0 0 3px rgba(214,243,47,0.12); }
        .v3-bl-lines { display: flex; flex-direction: column; gap: 8px; }
        .v3-bl-line { display: flex; gap: 8px; align-items: center; padding: 8px; background: rgba(0,0,0,0.2); border: 1px solid rgba(255,255,255,0.05); border-radius: 10px; }
        .v3-bl-line input { padding: 9px 11px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font: 500 12px Inter; }
        .v3-bl-line-total { color: #d6f32f; font-weight: 700; font-size: 12px; }
        .v3-bl-totals { padding: 14px 16px; background: rgba(0,0,0,0.3); border: 1px solid rgba(214,243,47,0.15); border-radius: 10px; display: flex; flex-direction: column; gap: 6px; }
        .v3-bl-totals > div { display: flex; justify-content: space-between; font: 500 12px Inter; color: #94a3b8; }
        .v3-bl-totals > div strong { color: #fff; font-weight: 700; }
        .v3-bl-totals-ttc { padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08); color: #d6f32f !important; font-size: 14px !important; }
        .v3-bl-totals-ttc strong { color: #d6f32f !important; font-weight: 800 !important; font-size: 14px; }
        .v3-btn-sm { padding: 6px 10px !important; font-size: 11px !important; }
        .v3-toast { position: fixed; bottom: 24px; right: 24px; padding: 14px 20px; background: #0f1e3a; color: #fff; border-radius: 12px; display: inline-flex; align-items: center; gap: 10px; font: 600 13px Inter; z-index: 2000; box-shadow: 0 12px 32px rgba(0,0,0,0.5); animation: v3-toast-in .3s ease-out; }
        @keyframes v3-toast-in { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .v3-toast-success { border: 1px solid rgba(34,197,94,0.4); color: #86efac; }
        .v3-toast-error { border: 1px solid rgba(239,68,68,0.4); color: #fca5a5; }
        .v3-icon-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #cbd5e1; padding: 6px 8px; border-radius: 8px; cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; justify-content: center; }
        .v3-icon-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .v3-icon-btn-danger:hover { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.3); color: #fca5a5; }
        .v3-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="v3-form-field">
      <label className="v3-form-field-label">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
      </div>
    </div>
  );
}
